import { AddressFragment, OrderDetailsFragment } from "@dashboard/graphql";
import { CSSProperties, forwardRef } from "react";
import { useIntl } from "react-intl";

import { getPickupPin } from "../../utils/delivery";
import { orderPrintMessages as messages } from "./messages";
import { getAddressLines, getAddressName, OrderPrintPaper } from "./utils";

interface PickupTicketProps {
  order: OrderDetailsFragment;
  shopName: string;
  companyAddress: AddressFragment | null | undefined;
  paper: OrderPrintPaper;
  printedAt: Date;
}

const DATE_TIME: Intl.DateTimeFormatOptions = {
  dateStyle: "short",
  timeStyle: "short",
} as Intl.DateTimeFormatOptions;

const rule: CSSProperties = { borderTop: "0.3mm dashed #000", margin: "2.5mm 0" };
const caption: CSSProperties = {
  fontSize: "0.8em",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
};
const blankLine: CSSProperties = { borderBottom: "0.3mm solid #000", height: "7mm" };

// Receipt-style ticket staff keep with an in-store pickup order. Printed output:
// plain black-on-white HTML sized in mm, independent of the dashboard theme.
export const PickupTicket = forwardRef<HTMLDivElement, PickupTicketProps>(
  ({ order, shopName, companyAddress, paper, printedAt }, ref) => {
    const intl = useIntl();
    const pin = getPickupPin(order.privateMetadata);
    const customer = order.shippingAddress ?? order.billingAddress;
    const customerName = getAddressName(customer);
    const phone = customer?.phone;
    const email = order.userEmail ?? order.user?.email;
    const itemCount = order.lines.reduce((sum, line) => sum + line.quantity, 0);
    const shopLines = getAddressLines(companyAddress, {
      withName: false,
      homeCountryCode: companyAddress?.country?.code,
    }).filter(line => line !== shopName);

    return (
      <div
        ref={ref}
        style={{
          boxSizing: "border-box",
          width: `${paper.contentWidthMm}mm`,
          padding: paper.pageHeightMm ? 0 : "3mm 3.5mm 6mm",
          fontFamily: "Helvetica, Arial, sans-serif",
          fontSize: `${9 * paper.fontScale}pt`,
          lineHeight: 1.3,
          color: "#000",
          background: "#fff",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 700, fontSize: "1.4em" }}>{shopName}</div>
          {shopLines.map((line, index) => (
            <div key={`${index}-${line}`} style={{ fontSize: "0.85em" }}>
              {line}
            </div>
          ))}
        </div>

        <div style={rule} />
        <div style={{ textAlign: "center" }}>
          <div style={{ ...caption, fontSize: "1.1em" }}>
            {intl.formatMessage(messages.ticketHeading)}
          </div>
          <div style={{ marginTop: "2mm" }}>{intl.formatMessage(messages.ticketOrder)}</div>
          <div style={{ fontWeight: 700, fontSize: "2.6em", lineHeight: 1.1 }}>#{order.number}</div>
          <div style={{ fontSize: "0.85em" }}>
            {intl.formatMessage(messages.ticketOrderDate, {
              date: intl.formatDate(order.created, DATE_TIME),
            })}
          </div>
        </div>

        <div style={rule} />
        <div style={caption}>{intl.formatMessage(messages.ticketCustomer)}</div>
        {customerName && <div style={{ fontWeight: 700, fontSize: "1.15em" }}>{customerName}</div>}
        {phone && <div>{intl.formatMessage(messages.phone, { phone })}</div>}
        {email && <div style={{ wordBreak: "break-all" }}>{email}</div>}

        <div style={rule} />
        <div style={{ textAlign: "center" }}>
          <div style={caption}>{intl.formatMessage(messages.ticketPin)}</div>
          <div
            style={{
              display: "inline-block",
              margin: "1.5mm 0",
              padding: "1mm 4mm",
              border: "0.6mm solid #000",
              fontFamily: "'Courier New', Courier, monospace",
              fontWeight: 700,
              fontSize: pin ? "2.8em" : "1.4em",
              letterSpacing: pin ? "0.3em" : undefined,
              lineHeight: 1.2,
            }}
          >
            {pin || intl.formatMessage(messages.ticketNoPin)}
          </div>
          {pin && (
            <div style={{ fontSize: "0.8em" }}>{intl.formatMessage(messages.ticketPinHint)}</div>
          )}
        </div>

        <div style={rule} />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={caption}>{intl.formatMessage(messages.ticketItems)}</div>
          <div style={{ fontSize: "0.85em" }}>
            {intl.formatMessage(messages.itemCount, { count: itemCount })}
          </div>
        </div>
        {order.lines.map(line => {
          const variantName = line.variant?.name?.trim();
          const showVariant =
            variantName && variantName !== line.productName && variantName !== line.productSku;

          return (
            <div key={line.id} style={{ display: "flex", gap: "2mm", marginTop: "1.5mm" }}>
              <div style={{ fontWeight: 700, minWidth: "7mm" }}>{line.quantity}×</div>
              <div style={{ flex: 1 }}>
                <div>
                  {line.productName}
                  {showVariant ? ` — ${variantName}` : ""}
                </div>
                {line.productSku && <div style={{ fontSize: "0.8em" }}>{line.productSku}</div>}
              </div>
            </div>
          );
        })}

        <div style={rule} />
        <div style={{ fontSize: "0.85em" }}>{intl.formatMessage(messages.ticketSignature)}</div>
        <div style={blankLine} />
        <div style={{ fontSize: "0.85em", marginTop: "2mm" }}>
          {intl.formatMessage(messages.ticketCollectedOn)}
        </div>
        <div style={blankLine} />

        <div style={{ textAlign: "center", fontSize: "0.85em", marginTop: "3mm" }}>
          {intl.formatMessage(messages.printedAt, {
            date: intl.formatDate(printedAt, DATE_TIME),
          })}
        </div>
      </div>
    );
  },
);

PickupTicket.displayName = "PickupTicket";
